import React from "react";
import { Star, TrendingUp, TrendingDown } from "lucide-react";
import { inr } from "@/lib/api";

export function VegDot({ veg, className = "" }) {
  const c = veg ? "border-veg" : "border-nonveg";
  const d = veg ? "bg-veg" : "bg-nonveg";
  return (
    <span className={`inline-flex items-center justify-center w-4 h-4 border-2 rounded-[3px] ${c} ${className}`} aria-label={veg ? "Veg" : "Non-veg"}>
      <span className={`w-1.5 h-1.5 rounded-full ${d}`} />
    </span>
  );
}

export function Stars({ value = 0, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      <Star size={size} className="fill-gold text-gold" />
      <span className="font-semibold text-ink text-sm">{Number(value).toFixed(1)}</span>
    </span>
  );
}

export function RatingPill({ value }) {
  return (
    <span className="inline-flex items-center gap-1 bg-veg text-white text-xs font-bold px-1.5 py-0.5 rounded-md">
      {Number(value).toFixed(1)} <Star size={11} className="fill-white" />
    </span>
  );
}

export function OfferBadge({ children }) {
  return (
    <span className="inline-block bg-secondary text-ink font-bold px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider">
      {children}
    </span>
  );
}

const STATUS_MAP = {
  placed: ["Placed", "bg-amber-100 text-amber-700"],
  confirmed: ["Confirmed", "bg-blue-100 text-blue-700"],
  preparing: ["Preparing", "bg-orange-100 text-orange-700"],
  ready: ["Ready", "bg-purple-100 text-purple-700"],
  picked_up: ["Picked Up", "bg-indigo-100 text-indigo-700"],
  on_the_way: ["On the Way", "bg-cyan-100 text-cyan-700"],
  delivered: ["Delivered", "bg-green-100 text-green-700"],
  cancelled: ["Cancelled", "bg-red-100 text-red-700"],
  active: ["Active", "bg-green-100 text-green-700"],
  pending: ["Pending", "bg-amber-100 text-amber-700"],
  suspended: ["Suspended", "bg-red-100 text-red-700"],
  paid: ["Paid", "bg-green-100 text-green-700"],
  refunded: ["Refunded", "bg-slate-100 text-slate-700"],
  failed: ["Failed", "bg-red-100 text-red-700"],
  approved: ["Approved", "bg-green-100 text-green-700"],
};

export function StatusBadge({ status }) {
  const [label, cls] = STATUS_MAP[status] || [status, "bg-slate-100 text-slate-700"];
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`} data-testid={`status-${status}`}>{label}</span>;
}

export function SectionHeader({ title, action, onAction }) {
  return (
    <div className="flex items-end justify-between px-4 mb-3">
      <h2 className="text-lg md:text-xl font-display font-bold text-ink tracking-tight">{title}</h2>
      {action && (
        <button onClick={onAction} className="text-sm font-semibold text-coral tap" data-testid="section-action">{action}</button>
      )}
    </div>
  );
}

export function Rail({ children, className = "" }) {
  return (
    <div className={`flex gap-3 overflow-x-auto hide-scrollbar snap-x-m px-4 pb-1 ${className}`}>
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, cta, onCta, testid }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid={testid || "empty-state"}>
      {Icon && <div className="w-20 h-20 rounded-full bg-coral-light flex items-center justify-center mb-4"><Icon className="text-coral" size={34} /></div>}
      <h3 className="text-lg font-display font-bold text-ink">{title}</h3>
      {subtitle && <p className="text-sm text-ink-600 mt-1 max-w-xs">{subtitle}</p>}
      {cta && <button onClick={onCta} className="mt-5 bg-coral hover:bg-coral-hover text-white font-semibold px-5 py-2.5 rounded-full tap" data-testid="empty-cta">{cta}</button>}
    </div>
  );
}

export function KpiCard({ icon: Icon, label, value, trend, tone = "coral" }) {
  const tones = { coral: "bg-coral-light text-coral", veg: "bg-emerald-50 text-veg", gold: "bg-amber-50 text-amber-600", ink: "bg-slate-100 text-ink" };
  return (
    <div className="bg-white rounded-2xl border border-border p-4 hover:shadow-sm transition-shadow" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>{Icon && <Icon size={20} />}</div>
        {trend != null && (
          <span className={`inline-flex items-center gap-1 text-xs font-bold ${trend >= 0 ? "text-veg" : "text-bad"}`}>
            {trend >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="mt-3 text-2xl font-display font-extrabold text-ink tracking-tight">{value}</div>
      <div className="text-xs text-ink-600 font-medium mt-0.5">{label}</div>
    </div>
  );
}

export function money(n) { return inr(n); }

export function SkelCard() {
  return (
    <div className="min-w-[260px] w-[260px]">
      <div className="skel h-36 rounded-2xl" />
      <div className="skel h-4 w-3/4 rounded mt-3" />
      <div className="skel h-3 w-1/2 rounded mt-2" />
    </div>
  );
}
